import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { LispCode } from "@/components/LispCode";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Bug, CircleNotch, Upload, Copy, Download, Warning, XCircle, CheckCircle } from "@phosphor-icons/react";

const downloadText = (text, name) => {
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

// LCS-based line diff -> [{type:'same'|'add'|'del', text}]
const diffLines = (aStr, bStr) => {
  const a = (aStr || "").split("\n");
  const b = (bStr || "").split("\n");
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--)
      dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
  const out = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { out.push({ type: "same", text: a[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: "del", text: a[i] }); i++; }
    else { out.push({ type: "add", text: b[j] }); j++; }
  }
  while (i < n) { out.push({ type: "del", text: a[i] }); i++; }
  while (j < m) { out.push({ type: "add", text: b[j] }); j++; }
  return out;
};

export const LispDebugger = ({ platform = "AutoCAD", version = "2025" }) => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [origCode, setOrigCode] = useState("");
  const [view, setView] = useState("fixed");
  const [mode, setMode] = useState("single");
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchResult, setBatchResult] = useState(null);
  const [batchJobId, setBatchJobId] = useState(null);
  const cancelRef = useRef(false);
  const jobRef = useRef(null);
  const fileRef = useRef();
  const batchRef = useRef();

  const loadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCode(await file.text());
    e.target.value = "";
  };

  const run = async () => {
    if (!code.trim()) { toast.error("Paste the AutoLISP you want debugged."); return; }
    setBusy(true); setResult(null); cancelRef.current = false; setOrigCode(code); setView("fixed");
    const t = toast.loading("Analyzing & fixing your AutoLISP…");
    try {
      const { job_id } = await api.debugLisp({ code, platform, version });
      jobRef.current = job_id;
      const started = Date.now();
      while (Date.now() - started < 240000) {
        for (let w = 0; w < 2000 && !cancelRef.current; w += 200) { await new Promise((r) => setTimeout(r, 200)); }
        if (cancelRef.current) { toast.message("Debug cancelled", { id: t }); return; }
        const s = await api.generationStatus(job_id);
        if (s.status === "done") { setResult(s.result); toast.success("Debug complete", { id: t }); return; }
        if (s.status === "error") { toast.error(s.error || "Debug failed", { id: t }); return; }
        if (s.status === "cancelled") { toast.message("Debug cancelled", { id: t }); return; }
      }
      toast.error("Debug timed out.", { id: t });
    } catch (err) { toast.error(err?.response?.data?.detail || "Debug failed", { id: t }); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    cancelRef.current = true; setBusy(false);
    toast.message("Debug cancelled");
    if (jobRef.current) { try { await api.cancelJob(jobRef.current); } catch (err) { console.debug("cancelJob failed (best-effort)", err); } }
  };

  const loadBatch = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const loaded = await Promise.all(files.slice(0, 10).map(async (f) => ({ name: f.name, code: await f.text() })));
    setBatchFiles((prev) => [...prev, ...loaded].slice(0, 10));
    e.target.value = "";
  };
  const removeBatch = (i) => setBatchFiles((fs) => fs.filter((_, idx) => idx !== i));
  const runBatch = async () => {
    if (!batchFiles.length) { toast.error("Load at least one .lsp file."); return; }
    setBusy(true); setBatchResult(null); cancelRef.current = false;
    const t = toast.loading(`Debugging ${batchFiles.length} file(s)…`);
    try {
      const { job_id } = await api.debugBatch({ files: batchFiles, platform, version });
      jobRef.current = job_id;
      const started = Date.now();
      while (Date.now() - started < 300000) {
        for (let w = 0; w < 2000 && !cancelRef.current; w += 200) { await new Promise((r) => setTimeout(r, 200)); }
        if (cancelRef.current) { toast.message("Debug cancelled", { id: t }); return; }
        const s = await api.generationStatus(job_id);
        if (s.status === "done") { setBatchResult(s.result?.results || []); setBatchJobId(job_id); toast.success("Batch debug complete", { id: t }); return; }
        if (s.status === "error") { toast.error(s.error || "Debug failed", { id: t }); return; }
        if (s.status === "cancelled") { toast.message("Debug cancelled", { id: t }); return; }
      }
      toast.error("Debug timed out.", { id: t });
    } catch (err) { toast.error(err?.response?.data?.detail || "Debug failed", { id: t }); }
    finally { setBusy(false); }
  };

  const issues = result?.issues || [];
  const errCount = issues.filter((i) => i.severity === "error").length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="lisp-debugger-btn"
          className="h-8 rounded-[3px] gap-1.5 text-xs font-mono text-[var(--text-dim)] hover:text-[var(--text)] hover:bg-[var(--surface-2)]">
          <Bug size={14} weight="bold" /> Debugger
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--surface)] border-[var(--border)] text-[var(--text)] max-w-3xl rounded-[4px]" data-testid="lisp-debugger-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-base flex items-center gap-2"><Bug size={18} weight="bold" className="text-[var(--primary)]" />LISP Debugger — paste broken .lsp, get an explained fix</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-xs">Diagnoses errors for {platform} {version} and returns a corrected, ready-to-load routine.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1.5">
          <button onClick={() => setMode("single")} data-testid="debug-mode-single"
            className={`h-7 px-3 rounded-[3px] text-[11px] font-mono border ${mode === "single" ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)]"}`}>Single</button>
          <button onClick={() => setMode("batch")} data-testid="debug-mode-batch"
            className={`h-7 px-3 rounded-[3px] text-[11px] font-mono border ${mode === "batch" ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)]"}`}>Batch (multiple .lsp)</button>
        </div>

        {mode === "single" && (
        <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <input type="file" accept=".lsp,.txt,text/*" ref={fileRef} className="hidden" onChange={loadFile} data-testid="debug-file-input" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current.click()} data-testid="debug-upload-btn"
            className="h-8 rounded-[3px] text-xs gap-1.5 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]"><Upload size={13} /> Load .lsp</Button>
          <Button size="sm" onClick={run} disabled={busy} data-testid="debug-run-btn"
            className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
            {busy ? <CircleNotch size={14} className="animate-spin" /> : <Bug size={14} />} Debug & Fix
          </Button>
          {busy && (
            <button onClick={cancel} data-testid="debug-cancel-btn"
              className="h-8 px-2.5 rounded-[3px] border border-[var(--alert)]/60 text-[var(--alert)] text-[11px] font-mono hover:bg-[var(--alert)]/10">Cancel</button>
          )}
        </div>

        <Textarea value={code} onChange={(e) => setCode(e.target.value)} data-testid="debug-input"
          placeholder="Paste the AutoLISP that errors or misbehaves…"
          className="min-h-[130px] max-h-[200px] resize-none rounded-[3px] bg-[var(--bg)] border-[var(--border)] font-mono text-[12px]" />

        {result && (
          <div className="flex flex-col gap-2" data-testid="debug-result">
            <div className="flex items-center gap-3 text-[12px] font-mono">
              {issues.length === 0
                ? <span className="text-[var(--success)] flex items-center gap-1" data-testid="debug-clean"><CheckCircle size={14} weight="fill" />No issues found</span>
                : <span className="text-[var(--alert)] flex items-center gap-1"><Warning size={14} weight="fill" />{errCount} error(s), {issues.length - errCount} warning(s)</span>}
              {result.validation && (
                <Badge className={`rounded-[2px] text-[10px] font-mono gap-1 px-1.5 border-0 ${result.validation.ok ? "bg-[var(--success)]/15 text-[var(--success)]" : "bg-[var(--alert)]/15 text-[var(--alert)]"}`}>
                  {result.validation.ok ? "Fixed code valid" : `${result.validation.errors.length} validation error(s)`}
                </Badge>
              )}
            </div>
            {result.summary && <p className="text-[12px] text-[var(--text)]/90 leading-relaxed" data-testid="debug-summary">{result.summary}</p>}
            {issues.length > 0 && (
              <div className="border border-[var(--border)] rounded-[3px] max-h-[130px] overflow-auto" data-testid="debug-issues">
                {issues.map((iss, i) => (
                  <div key={i} className="px-2.5 py-1.5 border-b border-[var(--border)]/40 text-[11px]" data-testid={`debug-issue-${i}`}>
                    <div className="flex items-center gap-1.5 font-mono">
                      {iss.severity === "error" ? <XCircle size={12} className="text-[var(--alert)]" /> : <Warning size={12} className="text-[var(--warn)]" />}
                      <span className="text-[var(--text-dim)]">line {iss.line ?? 0}</span>
                      <span className="text-[var(--text)]">{iss.problem}</span>
                    </div>
                    {iss.fix && <div className="text-[var(--text-dim)] mt-0.5 pl-4">→ {iss.fix}</div>}
                  </div>
                ))}
              </div>
            )}
            {result.corrected_code && (
              <div className="border border-[var(--border)] rounded-[3px]">
                <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-[var(--border)] bg-[var(--surface-2)]">
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => setView("fixed")} data-testid="debug-view-fixed"
                      className={`h-6 px-2 rounded-[3px] text-[11px] font-mono border ${view === "fixed" ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)]"}`}>Fixed code</button>
                    <button onClick={() => setView("diff")} data-testid="debug-view-diff"
                      className={`h-6 px-2 rounded-[3px] text-[11px] font-mono border ${view === "diff" ? "border-[var(--primary)] text-[var(--primary)] bg-[var(--primary)]/10" : "border-[var(--border)] text-[var(--text-dim)]"}`}>Diff</button>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(result.corrected_code); toast.success("Copied"); }} data-testid="debug-copy-btn" className="h-6 rounded-[3px] text-[11px] gap-1"><Copy size={12} />Copy</Button>
                    <Button size="sm" onClick={() => downloadText(result.corrected_code, "lispforge_fixed.lsp")} data-testid="debug-download-btn" className="h-6 rounded-[3px] text-[11px] gap-1 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"><Download size={12} />.lsp</Button>
                  </div>
                </div>
                {view === "fixed" ? (
                  <ScrollArea className="h-[170px]"><LispCode code={result.corrected_code} /></ScrollArea>
                ) : (
                  <ScrollArea className="h-[170px]">
                    <pre className="text-[11px] font-mono leading-relaxed" data-testid="debug-diff">
                      {diffLines(origCode, result.corrected_code).map((d, i) => (
                        <div key={i}
                          className={d.type === "add" ? "bg-[var(--success)]/15 text-[var(--success)] px-3" : d.type === "del" ? "bg-[var(--alert)]/15 text-[var(--alert)] px-3" : "text-[var(--text-dim)] px-3"}>
                          <span className="select-none opacity-60 mr-2">{d.type === "add" ? "+" : d.type === "del" ? "−" : " "}</span>{d.text || " "}
                        </div>
                      ))}
                    </pre>
                  </ScrollArea>
                )}
              </div>
            )}
          </div>
        )}
        </div>
        )}

        {mode === "batch" && (
          <div className="flex flex-col gap-2" data-testid="debug-batch">
            <div className="flex items-center gap-2">
              <input type="file" accept=".lsp,.txt,text/*" multiple ref={batchRef} className="hidden" onChange={loadBatch} data-testid="debug-batch-input" />
              <Button size="sm" variant="outline" onClick={() => batchRef.current.click()} data-testid="debug-batch-upload-btn"
                className="h-8 rounded-[3px] text-xs gap-1.5 border-[var(--border)] bg-transparent hover:bg-[var(--surface-2)]"><Upload size={13} /> Add .lsp files</Button>
              <Button size="sm" onClick={runBatch} disabled={busy || !batchFiles.length} data-testid="debug-batch-run-btn"
                className="h-8 rounded-[3px] text-xs gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                {busy ? <CircleNotch size={14} className="animate-spin" /> : <Bug size={14} />} Debug All ({batchFiles.length})
              </Button>
              {busy && (
                <button onClick={cancel} data-testid="debug-batch-cancel-btn"
                  className="h-8 px-2.5 rounded-[3px] border border-[var(--alert)]/60 text-[var(--alert)] text-[11px] font-mono hover:bg-[var(--alert)]/10">Cancel</button>
              )}
              <span className="text-[11px] font-mono text-[var(--text-dim)] ml-auto">{batchFiles.length}/10</span>
            </div>

            {batchFiles.length > 0 && !batchResult && (
              <div className="border border-[var(--border)] rounded-[3px] max-h-[130px] overflow-auto" data-testid="debug-batch-files">
                {batchFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between px-2.5 py-1.5 border-b border-[var(--border)]/40 text-[12px] font-mono">
                    <span className="truncate">{f.name}</span>
                    <button onClick={() => removeBatch(i)} data-testid={`debug-batch-del-${i}`} className="text-[var(--text-dim)] hover:text-[var(--alert)]"><XCircle size={14} /></button>
                  </div>
                ))}
              </div>
            )}

            {batchResult && (
              <div className="flex flex-col gap-2 max-h-[360px] overflow-auto" data-testid="debug-batch-result">
                <div className="flex items-center gap-2 sticky top-0 bg-[var(--surface)] py-1">
                  <a href={api.debugBatchZipUrl(batchJobId)} download data-testid="debug-batch-zip-btn"
                    className="h-7 px-2.5 rounded-[3px] text-[11px] font-mono flex items-center gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white">
                    <Download size={12} /> Download all fixed (.zip)
                  </a>
                  <button onClick={() => { setBatchResult(null); setBatchJobId(null); setBatchFiles([]); }} data-testid="debug-batch-new-btn"
                    className="h-7 px-2.5 rounded-[3px] text-[11px] font-mono border border-[var(--border)] text-[var(--text-dim)] hover:text-[var(--text)]">New batch</button>
                  <span className="text-[11px] font-mono text-[var(--text-dim)] ml-auto">{batchResult.length} file(s)</span>
                </div>
                {batchResult.map((r, i) => {
                  const errs = (r.issues || []).filter((x) => x.severity === "error").length;
                  return (
                    <div key={i} className="border border-[var(--border)] rounded-[3px]" data-testid={`debug-batch-item-${i}`}>
                      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-[var(--border)] bg-[var(--surface-2)]">
                        <span className="text-[12px] font-mono truncate flex items-center gap-1.5">
                          {(r.issues || []).length === 0 ? <CheckCircle size={13} className="text-[var(--success)]" /> : <Warning size={13} className="text-[var(--alert)]" />}
                          {r.name} <span className="text-[var(--text-dim)]">— {errs} err, {(r.issues || []).length - errs} warn</span>
                        </span>
                        <div className="flex gap-1.5">
                          {r.validation && <Badge className={`rounded-[2px] text-[9px] font-mono px-1 border-0 ${r.validation.ok ? "bg-[var(--success)]/15 text-[var(--success)]" : "bg-[var(--alert)]/15 text-[var(--alert)]"}`}>{r.validation.ok ? "valid" : "err"}</Badge>}
                          <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(r.corrected_code || ""); toast.success("Copied"); }} data-testid={`debug-batch-copy-${i}`} className="h-6 rounded-[3px] text-[10px] gap-1"><Copy size={11} />Copy</Button>
                          <Button size="sm" onClick={() => downloadText(r.corrected_code || "", (r.name || `fixed_${i}`).replace(/\.[^.]*$/, "") + "_fixed.lsp")} data-testid={`debug-batch-download-${i}`} className="h-6 rounded-[3px] text-[10px] gap-1 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"><Download size={11} />.lsp</Button>
                        </div>
                      </div>
                      {r.summary && <p className="text-[11px] text-[var(--text)]/90 px-2.5 py-1.5">{r.summary}</p>}
                      <ScrollArea className="h-[130px]"><LispCode code={r.corrected_code || ""} /></ScrollArea>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
